import { Reveal, SegmentedControl } from "@hana/ui";
import { type FormEvent, useMemo, useState } from "react";

type SearchItem = {
  id: number;
  // site は 51 brand まで拡張済みだが、UI では細かい brand 色分けをせず
  // 単に文字列表示するので string で受ける。
  site: string;
  title: string;
  description: string;
  pointText: string;
  url: string;
  crawledAt?: string;
  rank?: number;
  similarity?: number;
};

type SearchMode = "keyword" | "rag";
type SortMode = "relevance" | "point_desc" | "newest";

const SORT_LABELS: Record<SortMode, string> = {
  relevance: "関連度順",
  point_desc: "ポイント・還元率が高い順",
  newest: "新着順",
};

// pointText は自由テキスト (例: "10,000P", "1.5%", "楽天カード 3,000pt 詳細")。
// P/pt/ポイント/円 のついた数値と % を別グループとして拾い、単位あり > % > 数値なし
// の順に並べる (1.5% と 3P を同じスケールで比べないため)。
const POINT_UNIT_RE = /(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?\s*(?:p\b|pt\b|ポイント|円)/gi;
const POINT_PERCENT_RE = /(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?\s*%/g;

const toNumber = (int: string, frac: string | undefined) => {
  const n = Number(int.replace(/,/g, ""));
  const f = frac ? Number(`0.${frac}`) : 0;
  return Number.isFinite(n) ? n + f : Number.NaN;
};

const maxMatch = (text: string, re: RegExp): number | null => {
  const values = [...text.matchAll(re)]
    .map((m) => toNumber(m[1], m[2]))
    .filter((n) => Number.isFinite(n));
  return values.length > 0 ? Math.max(...values) : null;
};

type PointKey = { group: 0 | 1 | 2; value: number };

const pointSortKey = (text: string): PointKey => {
  if (!text) return { group: 2, value: 0 };
  const unit = maxMatch(text, POINT_UNIT_RE);
  if (unit !== null) return { group: 0, value: unit };
  const pct = maxMatch(text, POINT_PERCENT_RE);
  if (pct !== null) return { group: 1, value: pct };
  return { group: 2, value: 0 };
};

// D1 の current_timestamp は "YYYY-MM-DD HH:MM:SS" (UTC) を吐く。Date.parse の
// 挙動がブラウザ間で揺れるので、T 区切り + Z に正規化してからパースする。
const parseTimestamp = (value: string | undefined): number => {
  if (!value) return Number.NEGATIVE_INFINITY;
  const t = Date.parse(value.includes("T") ? value : `${value.replace(" ", "T")}Z`);
  return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
};

const sortItems = (items: SearchItem[], mode: SortMode): SearchItem[] => {
  if (mode === "relevance") return items;
  const copy = [...items];
  if (mode === "point_desc") {
    copy.sort((a, b) => {
      const ak = pointSortKey(a.pointText);
      const bk = pointSortKey(b.pointText);
      if (ak.group !== bk.group) return ak.group - bk.group;
      return bk.value - ak.value;
    });
  } else {
    copy.sort((a, b) => parseTimestamp(b.crawledAt) - parseTimestamp(a.crawledAt));
  }
  return copy;
};

const ENDPOINTS: Record<SearchMode, string> = {
  keyword: "/api/search",
  rag: "/api/rag",
};

const PLACEHOLDERS: Record<SearchMode, string> = {
  keyword: "例: 年会費無料 高還元",
  rag: "例: 年会費無料で還元率が高いクレカは?",
};

const MODE_SEGMENTS = [
  { value: "keyword" as const, label: "キーワード検索" },
  { value: "rag" as const, label: "AI検索" },
];

const MCP_ENDPOINT = "https://hana-mcp.toof.workers.dev/mcp";

const MCP_CONFIG_CLAUDE_CODE = JSON.stringify(
  { mcpServers: { hana: { url: MCP_ENDPOINT } } },
  null,
  2,
);

const MCP_CONFIG_STDIO_PROXY = JSON.stringify(
  { mcpServers: { hana: { command: "npx", args: ["mcp-remote", MCP_ENDPOINT] } } },
  null,
  2,
);

const MCP_TOOLS: { name: string; description: string }[] = [
  { name: "list_sites", description: "クロール対象サイトの識別子一覧を返す" },
  {
    name: "search_offers",
    description: "キーワードの部分一致 (ILIKE) で案件を検索する",
  },
  {
    name: "semantic_search_offers",
    description: "Workers AI (bge-m3) + pgvector 相当のベクトル類似検索",
  },
];

export function App() {
  const [query, setQuery] = useState("クレジットカード");
  const [mode, setMode] = useState<SearchMode>("keyword");
  const [sort, setSort] = useState<SortMode>("relevance");
  const [items, setItems] = useState<SearchItem[]>([]);
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);

  const hasQuery = useMemo(() => query.trim().length > 0, [query]);
  const sortedItems = useMemo(() => sortItems(items, sort), [items, sort]);

  const runSearch = async () => {
    if (!hasQuery) {
      setItems([]);
      setAnswer("");
      return;
    }

    setLoading(true);
    setError("");
    setSearched(true);
    setAnswer("");
    try {
      const res = await fetch(`${ENDPOINTS[mode]}?q=${encodeURIComponent(query.trim())}`);
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `search failed: ${res.status}`);
      }
      const data = (await res.json()) as {
        items: SearchItem[];
        answer?: string;
      };
      setItems(data.items ?? []);
      setAnswer(data.answer ?? "");
    } catch (e) {
      setError(e instanceof Error ? e.message : "unknown error");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await runSearch();
  };

  return (
    <div className="app">
      <header className="hero">
        <span className="brand">hana</span>
        <h1>横断ポイント検索</h1>
        <p className="lede">
          dokotoku.jp が対象とするポイ活サイト群から、キーワードと自然文の両方で案件を検索できます。
        </p>
      </header>

      {/*
       * 検索欄は浮いているクロマとして扱う。固定の帯でコンテンツ領域を食い潰すのではなく、
       * 結果がこの下を通り抜けてスクロールしていく。
       */}
      <div className="searchDock scrollEdge">
        <div className="searchDockInner material">
          <SegmentedControl
            segments={MODE_SEGMENTS}
            value={mode}
            onChange={setMode}
            label="検索モード"
          />
          <form className="field searchField" onSubmit={onSubmit}>
            <input
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              placeholder={PLACEHOLDERS[mode]}
              aria-label="検索キーワード"
            />
            <button type="submit" className="btn primary" disabled={loading}>
              {loading ? "検索中…" : "検索"}
            </button>
          </form>
        </div>
      </div>

      <main className="content">
        {mode === "rag" && answer ? (
          <Reveal>
            <section className="answer card">
              <h2>AIによる回答</h2>
              <p>{answer}</p>
            </section>
          </Reveal>
        ) : null}

        {searched ? (
          <div className={`meta${error ? " error" : ""}`}>
            <span>
              {error ? `エラー: ${error}` : loading ? "検索中…" : `${items.length}件ヒット`}
            </span>
            {items.length > 1 && !error && !loading ? (
              <label className="sort caption">
                並び替え
                <select
                  className="select"
                  value={sort}
                  onChange={(e) => setSort(e.currentTarget.value as SortMode)}
                >
                  {(Object.keys(SORT_LABELS) as SortMode[]).map((key) => (
                    <option key={key} value={key}>
                      {SORT_LABELS[key]}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
        ) : null}

        {items.length === 0 ? (
          <div className="empty">
            {searched && !loading && !error
              ? "該当する案件が見つかりませんでした。キーワードを変えて試してください。"
              : "検索語を入力して「検索」を押してください。"}
          </div>
        ) : (
          <section className="grid">
            {sortedItems.map((item, i) => (
              <Reveal key={`${item.site}-${item.id}`} index={i}>
                <article className="offer card">
                  <div className="offerHead">
                    <h2>{item.title}</h2>
                    <span className="badge">{item.site}</span>
                  </div>
                  <p className="offerDesc">{item.description || "説明なし"}</p>
                  <div className="offerFoot">
                    <span className={`point${item.pointText ? "" : " none"}`}>
                      {item.pointText || "ポイント表記なし"}
                    </span>
                    <a className="offerLink" href={item.url} target="_blank" rel="noreferrer">
                      案件ページを見る
                    </a>
                  </div>
                </article>
              </Reveal>
            ))}
          </section>
        )}

        <details className="mcpDocs panel">
          <summary>Claude / MCP クライアントから hana を使う</summary>
          <div className="mcpDocsBody">
            <p>
              hana は{" "}
              <a href="https://modelcontextprotocol.io/" target="_blank" rel="noreferrer">
                Model Context Protocol
              </a>{" "}
              の Streamable HTTP エンドポイントを公開しています。以下 3 つのツールが LLM
              エージェントから直接呼び出せます。
            </p>

            <div className="mcpEndpoint">
              <span className="mcpEndpointLabel">エンドポイント</span>
              <code>{MCP_ENDPOINT}</code>
            </div>

            <h3>Claude Code / HTTP 対応 MCP クライアント</h3>
            <p>
              <code>~/.config/claude-code/mcp.json</code> など、URL 指定に対応するクライアントはこの
              JSON で登録できます。
            </p>
            <pre>
              <code>{MCP_CONFIG_CLAUDE_CODE}</code>
            </pre>

            <h3>Claude Desktop など stdio のみ対応するクライアント</h3>
            <p>
              <code>claude_desktop_config.json</code> は HTTP transport を直接叩けないので、
              <code>mcp-remote</code> を経由させます (Node.js 20+ が必要)。
            </p>
            <pre>
              <code>{MCP_CONFIG_STDIO_PROXY}</code>
            </pre>

            <h3>公開ツール</h3>
            <ul className="mcpToolList">
              {MCP_TOOLS.map((tool) => (
                <li key={tool.name}>
                  <code>{tool.name}</code>
                  <span>{tool.description}</span>
                </li>
              ))}
            </ul>

            <p className="mcpDocsNote">
              現在 API キーによる認証は無いので、レート制限や乱用対策は上流の Cloudflare
              側で行っています。
            </p>
          </div>
        </details>
      </main>
    </div>
  );
}
